import { apiClient } from '@/shared/api/client';
import type { RegisterBookData } from '../types/myRegister.types';
import type { EntityOption } from '../../api/registerApi';

/**
 * MY Register Book 회사 목록 — L_ENTITY (권한 켜짐 시 L_USER_ENTITY 필터, MIG 미사용)
 */
export const fetchRegisterBookCompanies = async (): Promise<EntityOption[]> => {
    const response = await apiClient.get<{ data: { entities: EntityOption[] } }>('/register/my/js/companies');
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch register book companies');
    }
    return response.data?.data?.entities ?? (response.data as any)?.entities ?? [];
};

/**
 * Fetch register book data for a company.
 * - entityCd: value used by selector (typically L_ENTITY entityCd when grouped)
 * - entityCdL/entityCdMig: when available, pass both so backend can query L_REGISTER and MIG tables appropriately
 */
export const fetchRegisterBookData = async (
    entityCd: string,
    opts?: { entityCdL?: string | null; entityCdMig?: string | null; regNo?: string | null }
): Promise<RegisterBookData> => {
    const params: Record<string, any> = {};
    if (opts?.entityCdL) params.entityCdL = opts.entityCdL;
    if (opts?.entityCdMig) params.entityCdMig = opts.entityCdMig;
    if (opts?.regNo) params.regNo = opts.regNo;

    const response = await apiClient.get<RegisterBookData>(`/register/book/${encodeURIComponent(entityCd)}`, { params });
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch register book data');
    }
    return response.data ?? {};
};

/** Register Book 레거시 멤버 — A50011 주식 (S2000: RGST_SHARE_CD = 멤버 RGST_CD) */
export const fetchRegisterBookMemberShares = async (
    entityCd: string,
    memberRgstCd: string,
    opts?: {
        contactRegNo?: string;
        entityCdL?: string | null;
        entityCdMig?: string | null;
        regNo?: string | null;
    },
): Promise<Record<string, unknown>[]> => {
    const params: Record<string, string> = {};
    if (opts?.contactRegNo) params.contactRegNo = opts.contactRegNo;
    if (opts?.entityCdL) params.entityCdL = opts.entityCdL;
    if (opts?.entityCdMig) params.entityCdMig = opts.entityCdMig;
    if (opts?.regNo) params.regNo = opts.regNo;

    const response = await apiClient.get<{ shares?: Record<string, unknown>[] }>(
        `/register/book/${encodeURIComponent(entityCd)}/members/${encodeURIComponent(memberRgstCd)}/shares`,
        { params },
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch register book member shares');
    }
    const body = response.data as { shares?: Record<string, unknown>[] } | Record<string, unknown>[] | undefined;
    const payload = Array.isArray(body) ? { shares: body } : (body ?? {});
    return Array.isArray(payload.shares) ? payload.shares : [];
};

/**
 * Fetch allowed KEY_NAME list from L_CONTACT_KEY_MAP for a legacy KEY_TYPE_NAME.
 * Example: keyTypeName='Register of Directors'
 */
export type ClientRegisterSourceFlags = {
    showSpcFilter: boolean;
    showS2000Filter: boolean;
};

/**
 * L_CLIENT_SETTINGS(IS_SPC, IS_S2000) 기반 — MY Register 소스 필터 체크박스 표시 여부
 */
export type MyRegisterEntityAddressesPayload = {
    registeredAddress: Record<string, unknown>;
    alternateAddress: Record<string, unknown>;
    membersKeptAddress: Record<string, unknown>;
    financialRecordsKeptAddress: Record<string, unknown>;
    entityBranches: Array<Record<string, unknown>>;
};

export type MyRegisterContactAddressSavePayload = {
    positionCd: string;
    positionName?: string;
    categoryCd?: string;
    residential?: unknown[];
    service?: unknown[];
    business?: unknown[];
    other?: unknown[];
};

export type MyRegisterContactAddressesResponse = {
    entityCd: string;
    contactCd: string;
    positionCd: string;
    contactLike: Record<string, unknown>;
};

/**
 * MY Register — L_CONTACT_ADDRESS + L_CONTACT_BRANCH_MAP 조회 (Contact API와 분리)
 */
export const fetchMyRegisterContactAddresses = async (
    entityCd: string,
    contactCd: string,
    positionCd: string,
    categoryCd?: string,
): Promise<MyRegisterContactAddressesResponse> => {
    const params: Record<string, string> = { positionCd };
    if (categoryCd) params.categoryCd = categoryCd;

    const response = await apiClient.get<unknown>(
        `/register/my/contact-addresses/${encodeURIComponent(entityCd)}/${encodeURIComponent(contactCd)}`,
        { params },
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch register contact addresses');
    }

    const body = response.data as Record<string, unknown> | undefined;
    const payload = (body?.data ?? body ?? {}) as Record<string, unknown>;
    const contactLike = (payload.contactLike ?? {}) as Record<string, unknown>;

    return {
        entityCd: String(payload.entityCd ?? entityCd),
        contactCd: String(payload.contactCd ?? contactCd),
        positionCd: String(payload.positionCd ?? positionCd),
        contactLike,
    };
};

/**
 * MY Register — L_CONTACT_ADDRESS + L_CONTACT_BRANCH_MAP 저장 (Contact API와 분리)
 */
export const saveMyRegisterContactAddresses = async (
    entityCd: string,
    contactCd: string,
    payload: MyRegisterContactAddressSavePayload,
): Promise<void> => {
    const response = await apiClient.put<unknown>(
        `/register/my/contact-addresses/${encodeURIComponent(entityCd)}/${encodeURIComponent(contactCd)}`,
        payload,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to save register contact addresses');
    }
};

/**
 * MY Register — 엔티티 프로필 주소(등기/사업장/지점/명부보관) 읽기 전용
 */
export type MyRegisterEntityDirectorRow = {
    OFFICER_IDX: number;
    CONTACT_CD: string;
    CONTACT_REG_NO: string;
    CONTACT_NAME: string;
    ALTERNATE_TO_DIRECTOR: string;
    APPOINTMENT_DATE: string;
    CESSATION_DATE: string;
    _ENTITY_OFFICER?: boolean;
    _RB_SOURCE?: string;
};

/**
 * MY Register — L_OFFICER(POSITION_CD=B30027) 디렉터 목록
 */
export const fetchMyRegisterEntityDirectors = async (
    entityCd: string,
): Promise<MyRegisterEntityDirectorRow[]> => {
    const response = await apiClient.get<unknown>(
        `/register/my/entity-directors/${encodeURIComponent(entityCd)}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch entity directors');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const payload = (body?.data ?? body ?? {}) as { directors?: MyRegisterEntityDirectorRow[] };
    return Array.isArray(payload.directors) ? payload.directors : [];
};

/**
 * MY Register — 디렉터 상세 (Register DirectorSection jsonData 형태)
 */
export const fetchMyRegisterEntityDirectorDetail = async (
    entityCd: string,
    officerIdx: number,
): Promise<Record<string, unknown>> => {
    const response = await apiClient.get<unknown>(
        `/register/my/entity-directors/${encodeURIComponent(entityCd)}/${officerIdx}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch entity director detail');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const payload = (body?.data ?? body ?? {}) as { director?: Record<string, unknown> };
    return (payload.director as Record<string, unknown>) || {};
};

/**
 * MY Register — 디렉터 소프트 삭제 (L_OFFICER.IS_USE_DEL = 1)
 */
export const deleteMyRegisterEntityDirector = async (
    entityCd: string,
    officerIdx: number,
): Promise<void> => {
    const response = await apiClient.delete<unknown>(
        `/register/my/entity-directors/${encodeURIComponent(entityCd)}/${officerIdx}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to delete entity director');
    }
};

/**
 * MY Register — 비서 소프트 삭제 (L_OFFICER.IS_USE_DEL = 1)
 */
export const deleteMyRegisterEntitySecretary = async (
    entityCd: string,
    officerIdx: number,
): Promise<void> => {
    const response = await apiClient.delete<unknown>(
        `/register/my/entity-secretaries/${encodeURIComponent(entityCd)}/${officerIdx}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to delete entity secretary');
    }
};

/**
 * MY Register — 감사인 소프트 삭제 (L_OFFICER.IS_USE_DEL = 1)
 */
export const deleteMyRegisterEntityAuditor = async (
    entityCd: string,
    officerIdx: number,
): Promise<void> => {
    const response = await apiClient.delete<unknown>(
        `/register/my/entity-auditors/${encodeURIComponent(entityCd)}/${officerIdx}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to delete entity auditor');
    }
};

/**
 * MY Register — 세무대리인 소프트 삭제 (L_OFFICER.IS_USE_DEL = 1)
 */
export const deleteMyRegisterEntityTaxAgent = async (
    entityCd: string,
    officerIdx: number,
): Promise<void> => {
    const response = await apiClient.delete<unknown>(
        `/register/my/entity-tax-agents/${encodeURIComponent(entityCd)}/${officerIdx}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to delete entity tax agent');
    }
};

/**
 * MY Register — 매니저 소프트 삭제 (L_OFFICER.IS_USE_DEL = 1)
 */
export const deleteMyRegisterEntityManager = async (
    entityCd: string,
    officerIdx: number,
): Promise<void> => {
    const response = await apiClient.delete<unknown>(
        `/register/my/entity-managers/${encodeURIComponent(entityCd)}/${officerIdx}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to delete entity manager');
    }
};

/**
 * MY Register — 수익적소유자(Beneficial Owner) 소프트 삭제 (L_BENEFICIALOWNER.IS_USE_DEL = 1)
 * CTRL_IDX 단위 삭제. 화면 한 줄이 Legal+Beneficial 두 CTRL_IDX면 호출측이 각각 호출.
 */
export const deleteMyRegisterEntityBeneficialOwner = async (
    entityCd: string,
    ctrlIdx: number,
): Promise<void> => {
    const response = await apiClient.delete<unknown>(
        `/register/my/entity-beneficial-owners/${encodeURIComponent(entityCd)}/${ctrlIdx}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to delete entity beneficial owner');
    }
};

/**
 * MY Register — 멤버(Member) 소프트 삭제 (L_SHAREHOLDER_DETAIL.IS_USE_DEL = 1)
 * update와 동일하게 SHAREHOLDER_IDX 기준 (줄당 1 idx).
 */
export const deleteMyRegisterEntityMember = async (
    entityCd: string,
    shareholderIdx: number,
): Promise<void> => {
    const response = await apiClient.delete<unknown>(
        `/register/my/entity-members/${encodeURIComponent(entityCd)}/${shareholderIdx}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to delete entity member');
    }
};

/**
 * MY Register — Shares Allotment 소프트 삭제 (L_SHAREHOLDER_DETAIL.IS_USE_DEL = 1)
 * update와 동일하게 SHAREHOLDER_IDX 기준 (줄당 1 idx).
 */
export const deleteMyRegisterEntityAllotment = async (
    entityCd: string,
    shareholderIdx: number,
): Promise<void> => {
    const response = await apiClient.delete<unknown>(
        `/register/my/entity-allotments/${encodeURIComponent(entityCd)}/${shareholderIdx}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to delete entity allotment');
    }
};

/**
 * MY Register — Shares Transfer 소프트 삭제 (L_SHAREHOLDER_DETAIL.IS_USE_DEL = 1)
 * transfer-in 행 1개(SHAREHOLDER_IDX) 기준.
 */
export const deleteMyRegisterEntityTransfer = async (
    entityCd: string,
    shareholderIdx: number,
): Promise<void> => {
    const response = await apiClient.delete<unknown>(
        `/register/my/entity-transfers/${encodeURIComponent(entityCd)}/${shareholderIdx}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to delete entity transfer');
    }
};

export type UpdateMyRegisterEntityDirectorOfficerPayload = {
    appointmentDate?: string | null;
    cessationDate?: string | null;
    designation?: string;
};

/**
 * MY Register — L_OFFICER 디렉터 임명일/퇴임일 저장
 */
export const updateMyRegisterEntityDirectorOfficer = async (
    entityCd: string,
    officerIdx: number,
    payload: UpdateMyRegisterEntityDirectorOfficerPayload,
): Promise<void> => {
    const response = await apiClient.put<unknown>(
        `/register/my/entity-directors/${encodeURIComponent(entityCd)}/${officerIdx}`,
        payload,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update entity director officer');
    }
};

/**
 * MY Register — profile history sync POST body
 */
export type MyRegisterProfileHistorySyncOpts = {
    cessationDate?: string | null;
    registerProfile?: Record<string, unknown>;
};

function buildProfileHistorySyncPostBody(opts?: MyRegisterProfileHistorySyncOpts): Record<string, unknown> {
    return {
        cessationDate: opts?.cessationDate ?? null,
        ...(opts?.registerProfile ? { registerProfile: opts.registerProfile } : {}),
    };
}

/**
 * MY Register — 퇴임 디렉터 Global Contact 반영 후 profile history 스냅샷
 */
export const syncMyRegisterEntityDirectorProfileHistory = async (
    entityCd: string,
    officerIdx: number,
    opts?: MyRegisterProfileHistorySyncOpts,
): Promise<{ historyId: string; action: string }> => {
    const response = await apiClient.post<unknown>(
        `/register/my/entity-directors/${encodeURIComponent(entityCd)}/${officerIdx}/profile-history`,
        buildProfileHistorySyncPostBody(opts),
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to sync entity director profile history');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const payload = (body?.data ?? body ?? {}) as { historyId?: string; action?: string };
    return {
        historyId: String(payload.historyId ?? ''),
        action: String(payload.action ?? ''),
    };
};

/**
 * MY Register — 퇴임 매니저 Global Contact 반영 후 profile history 스냅샷
 */
export const syncMyRegisterEntityManagerProfileHistory = async (
    entityCd: string,
    officerIdx: number,
    opts?: MyRegisterProfileHistorySyncOpts,
): Promise<{ historyId: string; action: string }> => {
    const response = await apiClient.post<unknown>(
        `/register/my/entity-managers/${encodeURIComponent(entityCd)}/${officerIdx}/profile-history`,
        buildProfileHistorySyncPostBody(opts),
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to sync entity manager profile history');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const payload = (body?.data ?? body ?? {}) as { historyId?: string; action?: string };
    return {
        historyId: String(payload.historyId ?? ''),
        action: String(payload.action ?? ''),
    };
};

export type MyRegisterEntitySecretaryRow = {
    OFFICER_IDX: number;
    CONTACT_CD: string;
    CONTACT_REG_NO: string;
    CONTACT_NAME: string;
    APPOINTMENT_DATE: string;
    CESSATION_DATE: string;
    _ENTITY_OFFICER?: boolean;
    _RB_SOURCE?: string;
};

/**
 * MY Register — L_OFFICER(POSITION_CD=B30033) 비서 목록
 */
export const fetchMyRegisterEntitySecretaries = async (
    entityCd: string,
): Promise<MyRegisterEntitySecretaryRow[]> => {
    const response = await apiClient.get<unknown>(
        `/register/my/entity-secretaries/${encodeURIComponent(entityCd)}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch entity secretaries');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const payload = (body?.data ?? body ?? {}) as { secretaries?: MyRegisterEntitySecretaryRow[] };
    return Array.isArray(payload.secretaries) ? payload.secretaries : [];
};

/**
 * MY Register — 비서 상세 (Register SecretariesSection jsonData 형태)
 */
export const fetchMyRegisterEntitySecretaryDetail = async (
    entityCd: string,
    officerIdx: number,
): Promise<Record<string, unknown>> => {
    const response = await apiClient.get<unknown>(
        `/register/my/entity-secretaries/${encodeURIComponent(entityCd)}/${officerIdx}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch entity secretary detail');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const payload = (body?.data ?? body ?? {}) as { secretary?: Record<string, unknown> };
    return (payload.secretary as Record<string, unknown>) || {};
};

export type UpdateMyRegisterEntitySecretaryOfficerPayload = {
    appointmentDate?: string | null;
    cessationDate?: string | null;
    occupation?: string;
    secretaryLicenseNo?: string;
    secretaryCertificateNo?: string;
};

/**
 * MY Register — L_OFFICER 비서 임명일/퇴임일 저장
 */
export const updateMyRegisterEntitySecretaryOfficer = async (
    entityCd: string,
    officerIdx: number,
    payload: UpdateMyRegisterEntitySecretaryOfficerPayload,
): Promise<void> => {
    const response = await apiClient.put<unknown>(
        `/register/my/entity-secretaries/${encodeURIComponent(entityCd)}/${officerIdx}`,
        payload,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update entity secretary officer');
    }
};

/**
 * MY Register — 퇴임 비서 Global Contact 반영 후 profile history 스냅샷
 */
export const syncMyRegisterEntitySecretaryProfileHistory = async (
    entityCd: string,
    officerIdx: number,
    opts?: MyRegisterProfileHistorySyncOpts,
): Promise<{ historyId: string; action: string }> => {
    const response = await apiClient.post<unknown>(
        `/register/my/entity-secretaries/${encodeURIComponent(entityCd)}/${officerIdx}/profile-history`,
        buildProfileHistorySyncPostBody(opts),
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to sync entity secretary profile history');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const payload = (body?.data ?? body ?? {}) as { historyId?: string; action?: string };
    return {
        historyId: String(payload.historyId ?? ''),
        action: String(payload.action ?? ''),
    };
};

export type MyRegisterEntityAuditorRow = {
    OFFICER_IDX: number;
    CONTACT_CD: string;
    CONTACT_REG_NO: string;
    CONTACT_NAME: string;
    APPOINTMENT_DATE: string;
    CESSATION_DATE: string;
    _ENTITY_OFFICER?: boolean;
    _RB_SOURCE?: string;
};

/**
 * MY Register — L_OFFICER(POSITION_CD=B30025) 감사 목록
 */
export const fetchMyRegisterEntityAuditors = async (
    entityCd: string,
): Promise<MyRegisterEntityAuditorRow[]> => {
    const response = await apiClient.get<unknown>(
        `/register/my/entity-auditors/${encodeURIComponent(entityCd)}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch entity auditors');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const payload = (body?.data ?? body ?? {}) as { auditors?: MyRegisterEntityAuditorRow[] };
    return Array.isArray(payload.auditors) ? payload.auditors : [];
};

/**
 * MY Register — 감사 상세 (Register AuditorsSection jsonData 형태)
 */
export const fetchMyRegisterEntityAuditorDetail = async (
    entityCd: string,
    officerIdx: number,
): Promise<Record<string, unknown>> => {
    const response = await apiClient.get<unknown>(
        `/register/my/entity-auditors/${encodeURIComponent(entityCd)}/${officerIdx}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch entity auditor detail');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const payload = (body?.data ?? body ?? {}) as { auditor?: Record<string, unknown> };
    return (payload.auditor as Record<string, unknown>) || {};
};

export type UpdateMyRegisterEntityAuditorOfficerPayload = {
    appointmentDate?: string | null;
    cessationDate?: string | null;
    firmNo?: string;
};

/**
 * MY Register — L_OFFICER 감사 임명일/퇴임일 저장
 */
export const updateMyRegisterEntityAuditorOfficer = async (
    entityCd: string,
    officerIdx: number,
    payload: UpdateMyRegisterEntityAuditorOfficerPayload,
): Promise<void> => {
    const response = await apiClient.put<unknown>(
        `/register/my/entity-auditors/${encodeURIComponent(entityCd)}/${officerIdx}`,
        payload,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update entity auditor officer');
    }
};

/**
 * MY Register — 퇴임 감사 Global Contact 반영 후 profile history 스냅샷
 */
export const syncMyRegisterEntityAuditorProfileHistory = async (
    entityCd: string,
    officerIdx: number,
    opts?: MyRegisterProfileHistorySyncOpts,
): Promise<{ historyId: string; action: string }> => {
    const response = await apiClient.post<unknown>(
        `/register/my/entity-auditors/${encodeURIComponent(entityCd)}/${officerIdx}/profile-history`,
        buildProfileHistorySyncPostBody(opts),
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to sync entity auditor profile history');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const payload = (body?.data ?? body ?? {}) as { historyId?: string; action?: string };
    return {
        historyId: String(payload.historyId ?? ''),
        action: String(payload.action ?? ''),
    };
};

export type MyRegisterEntityTaxAgentRow = {
    OFFICER_IDX: number;
    CONTACT_CD: string;
    CONTACT_REG_NO: string;
    CONTACT_NAME: string;
    APPOINTMENT_DATE: string;
    CESSATION_DATE: string;
    _ENTITY_OFFICER?: boolean;
    _RB_SOURCE?: string;
};

/**
 * MY Register — L_OFFICER(POSITION_CD=B30045) Tax Agent 목록
 */
export const fetchMyRegisterEntityTaxAgents = async (
    entityCd: string,
): Promise<MyRegisterEntityTaxAgentRow[]> => {
    const response = await apiClient.get<unknown>(
        `/register/my/entity-tax-agents/${encodeURIComponent(entityCd)}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch entity tax agents');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const payload = (body?.data ?? body ?? {}) as { taxAgents?: MyRegisterEntityTaxAgentRow[] };
    return Array.isArray(payload.taxAgents) ? payload.taxAgents : [];
};

/**
 * MY Register — Tax Agent 상세 (Director 필드 + 주소)
 */
export const fetchMyRegisterEntityTaxAgentDetail = async (
    entityCd: string,
    officerIdx: number,
): Promise<Record<string, unknown>> => {
    const response = await apiClient.get<unknown>(
        `/register/my/entity-tax-agents/${encodeURIComponent(entityCd)}/${officerIdx}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch entity tax agent detail');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const payload = (body?.data ?? body ?? {}) as { taxAgent?: Record<string, unknown> };
    return (payload.taxAgent as Record<string, unknown>) || {};
};

export type UpdateMyRegisterEntityTaxAgentOfficerPayload = {
    appointmentDate?: string | null;
    cessationDate?: string | null;
};

/**
 * MY Register — L_OFFICER Tax Agent 임명일/퇴임일 저장
 */
export const updateMyRegisterEntityTaxAgentOfficer = async (
    entityCd: string,
    officerIdx: number,
    payload: UpdateMyRegisterEntityTaxAgentOfficerPayload,
): Promise<void> => {
    const response = await apiClient.put<unknown>(
        `/register/my/entity-tax-agents/${encodeURIComponent(entityCd)}/${officerIdx}`,
        payload,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update entity tax agent officer');
    }
};

/**
 * MY Register — 퇴임 Tax Agent Global Contact 반영 후 profile history 스냅샷
 */
export const syncMyRegisterEntityTaxAgentProfileHistory = async (
    entityCd: string,
    officerIdx: number,
    opts?: MyRegisterProfileHistorySyncOpts,
): Promise<{ historyId: string; action: string }> => {
    const response = await apiClient.post<unknown>(
        `/register/my/entity-tax-agents/${encodeURIComponent(entityCd)}/${officerIdx}/profile-history`,
        buildProfileHistorySyncPostBody(opts),
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to sync entity tax agent profile history');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const payload = (body?.data ?? body ?? {}) as { historyId?: string; action?: string };
    return {
        historyId: String(payload.historyId ?? ''),
        action: String(payload.action ?? ''),
    };
};

export type MyRegisterEntityManagerRow = {
    OFFICER_IDX: number;
    CONTACT_CD: string;
    CONTACT_REG_NO: string;
    CONTACT_NAME: string;
    APPOINTMENT_DATE: string;
    CESSATION_DATE: string;
    _ENTITY_OFFICER?: boolean;
    _RB_SOURCE?: string;
};

/**
 * MY Register — L_OFFICER(POSITION_CD=B30035) 매니저 목록
 */
export const fetchMyRegisterEntityManagers = async (
    entityCd: string,
): Promise<MyRegisterEntityManagerRow[]> => {
    const response = await apiClient.get<unknown>(
        `/register/my/entity-managers/${encodeURIComponent(entityCd)}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch entity managers');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const payload = (body?.data ?? body ?? {}) as { managers?: MyRegisterEntityManagerRow[] };
    return Array.isArray(payload.managers) ? payload.managers : [];
};

/**
 * MY Register — 매니저 상세 (Register DirectorSection jsonData 형태)
 */
export const fetchMyRegisterEntityManagerDetail = async (
    entityCd: string,
    officerIdx: number,
): Promise<Record<string, unknown>> => {
    const response = await apiClient.get<unknown>(
        `/register/my/entity-managers/${encodeURIComponent(entityCd)}/${officerIdx}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch entity manager detail');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const payload = (body?.data ?? body ?? {}) as { manager?: Record<string, unknown> };
    return (payload.manager as Record<string, unknown>) || {};
};

export type UpdateMyRegisterEntityManagerOfficerPayload = {
    appointmentDate?: string | null;
    cessationDate?: string | null;
    occupation?: string;
};

/**
 * MY Register — L_OFFICER 매니저 임명일/퇴임일 저장
 */
export const updateMyRegisterEntityManagerOfficer = async (
    entityCd: string,
    officerIdx: number,
    payload: UpdateMyRegisterEntityManagerOfficerPayload,
): Promise<void> => {
    const response = await apiClient.put<unknown>(
        `/register/my/entity-managers/${encodeURIComponent(entityCd)}/${officerIdx}`,
        payload,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update entity manager officer');
    }
};

export type MyRegisterEntityMemberRow = {
    SHAREHOLDER_IDX: number;
    CONTACT_CD: string;
    CONTACT_REG_NO: string;
    MEMBER_CODE: string;
    CONTACT_NAME: string;
    MEMBER_NAME: string;
    APPOINTMENT_DATE: string;
    CESSATION_DATE: string;
    TOTAL_SHARES?: string | number;
    TOTAL_SHARES_PREF?: string | number;
    TOTAL_SHARES_OTHER?: string | number;
    PERCENT?: string | number;
    PERCENT_PREF?: string | number;
    PERCENT_OTHER?: string | number;
    _ENTITY_MEMBER?: boolean;
    _RB_SOURCE?: string;
};

/**
 * MY Register — L_SHAREHOLDER_DETAIL 멤버 목록
 */
export const fetchMyRegisterEntityMembers = async (
    entityCd: string,
): Promise<MyRegisterEntityMemberRow[]> => {
    const response = await apiClient.get<unknown>(
        `/register/my/entity-members/${encodeURIComponent(entityCd)}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch entity members');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const payload = (body?.data ?? body ?? {}) as { members?: MyRegisterEntityMemberRow[] };
    return Array.isArray(payload.members) ? payload.members : [];
};

export type CreateMyRegisterEntityMemberPayload = {
    contactCd: string;
    appointmentDate?: string | null;
};

/**
 * MY Register — Entity Member 신규 (L_SHAREHOLDER_DETAIL shell)
 */
export const createMyRegisterEntityMember = async (
    entityCd: string,
    payload: CreateMyRegisterEntityMemberPayload,
): Promise<Record<string, unknown>> => {
    const response = await apiClient.post<unknown>(
        `/register/my/entity-members/${encodeURIComponent(entityCd)}`,
        {
            contactCd: payload.contactCd,
            appointmentDate: payload.appointmentDate ?? null,
        },
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to create entity member');
    }
    const body = response.data as Record<string, unknown> | undefined;
    return (body?.data ?? body ?? {}) as Record<string, unknown>;
};

/**
 * MY Register — 멤버 상세 (MembersSection jsonData 형태, Global Contact 기준)
 */
export const fetchMyRegisterEntityMemberDetail = async (
    entityCd: string,
    shareholderIdx: number,
): Promise<Record<string, unknown>> => {
    const response = await apiClient.get<unknown>(
        `/register/my/entity-members/${encodeURIComponent(entityCd)}/${shareholderIdx}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch entity member detail');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const payload = (body?.data ?? body ?? {}) as { member?: Record<string, unknown> };
    return (payload.member as Record<string, unknown>) || {};
};

/**
 * MY Register — CONTACT_CD 기준 L_SHAREHOLDER_DETAIL 주식 목록 (MembersSection sharesList)
 */
export const fetchMyRegisterEntityMemberShares = async (
    entityCd: string,
    contactCd: string,
): Promise<Record<string, unknown>[]> => {
    const response = await apiClient.get<unknown>(
        `/register/my/entity-members/${encodeURIComponent(entityCd)}/contact/${encodeURIComponent(contactCd)}/shares`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch entity member shares');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const payload = (body?.data ?? body ?? {}) as { shares?: Record<string, unknown>[] };
    return Array.isArray(payload.shares) ? payload.shares : [];
};

export type MyRegisterTransferorCandidate = {
    CONTACT_CD: string;
    CONTACT_NAME: string;
    CONTACT_REG_NO: string;
    TFOR_CERTI_NO: string;
    TFOR_NAME: string;
    TFOR_REG_NO: string;
    TFOR_CONTACT_CD: string;
    TFOR_FOLIO_NO?: string;
    AVAILABLE_SHARES: string;
    TFOR_NUMBER_OF_SHARES: string;
    TFOR_PRICE_PER_SHARE: string;
    CLASS_OF_SHARES?: string;
    CLASS_OF_SHARES_TEXT?: string;
    CURRENCY_CD?: string;
    TFOR_STREET01?: string;
    TFOR_STREET02?: string;
    TFOR_STREET03?: string;
    TFOR_CITY?: string;
    TFOR_STATE?: string;
    TFOR_POST_CODE?: string;
    TFOR_COUNTRY?: string;
    TFOR_CITY_TEXT?: string;
    TFOR_STATE_TEXT?: string;
    TFOR_COUNTRY_CODE?: string;
    TFOR_STATE_CODE?: string;
    TFOR_CITY_CODE?: string;
    TFOR_POSTCODE_CODE?: string;
    TFOR_CATEGORY_CD?: string;
};

/**
 * MY Register — 엔티티 SHAREHOLDER_JSON_DATA 숫자 cert 최댓값 + 1
 */
export const fetchMyRegisterNextCertificateNo = async (
    entityCd: string,
    afterCertNo?: string,
): Promise<string> => {
    const query = afterCertNo
        ? `?afterCertNo=${encodeURIComponent(afterCertNo)}`
        : '';
    const response = await apiClient.get<unknown>(
        `/register/my/entity-members/${encodeURIComponent(entityCd)}/next-certificate-no${query}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch next certificate number');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const payload = (body?.data ?? body ?? {}) as Record<string, unknown>;
    const nested = payload.data as Record<string, unknown> | undefined;
    return String(
        nested?.nextCertificateNo
        ?? payload.nextCertificateNo
        ?? '',
    ).trim();
};

/**
 * MY Register — Transfer-in 양도인 후보 (잔여 주식 보유 멤버·인증서)
 */
export const fetchMyRegisterEntityTransferorCandidates = async (
    entityCd: string,
    excludeContactCd?: string,
): Promise<MyRegisterTransferorCandidate[]> => {
    const query = excludeContactCd
        ? `?excludeContactCd=${encodeURIComponent(excludeContactCd)}`
        : '';
    const response = await apiClient.get<unknown>(
        `/register/my/entity-members/${encodeURIComponent(entityCd)}/transferor-candidates${query}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch transferor candidates');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const payload = (body?.data ?? body ?? {}) as { candidates?: MyRegisterTransferorCandidate[] };
    return Array.isArray(payload.candidates) ? payload.candidates : [];
};

// ─── S2000 행 → LETTAGA Convert (/api/register/my/s2000-convert) ───

export type S2000ConvertCapitalBuckets = {
    SHARES_ORDINARY: number;
    SHARES_PREFERENCE: number;
    SHARES_OTHERS: number;
    PAID_UP_ORDINARY: number;
    PAID_UP_PREFERENCE: number;
    PAID_UP_OTHERS: number;
};

export type S2000ConvertTransferor = {
    contactCd: string;
    contactName: string;
    contactRegNo: string;
    certNo: string;
    lettagaCertNo: string;
    shares: number;
    /** 양도인 행 보유 버킷·납입액 — 부분 양도 잔여 행 자본금 계산용 */
    bucket: 'ordinary' | 'preference' | 'others';
    paidUp: number;
    pricePerShare: string;
    shareholderIdx: number;
    historyId: string;
};

export type S2000ConvertPrepared = {
    kind: 'allotment' | 'transfer';
    source: { rgstCd: string; registerWork: string; json: Record<string, unknown> };
    member: { rgstCd: string; json: Record<string, unknown> } | null;
    transferor: S2000ConvertTransferor | null;
    transfereeCert: { rgstCd: string; json: Record<string, unknown> } | null;
    willHideMember: boolean;
    capital: { current: S2000ConvertCapitalBuckets | null; shareholderSum: S2000ConvertCapitalBuckets } | null;
    preparedAt: string;
    warnings: string[];
    duplicateCandidates: { shareholderIdx: number; name: string; certNo: string; shares: number }[];
    certNoMatches: { shareholderIdx: number; name: string; certNo: string }[];
};

export type S2000ConvertPrepareResult =
    | { status: 'ready'; data: S2000ConvertPrepared }
    | { status: 'completePending'; shareholderIdx: number }
    | { status: 'blocked'; reason: string; extra: Record<string, unknown> };

/** 막힘 응답(400) 의 reason·추가 값 — 없으면 null (일반 오류) */
export function readS2000ConvertBlockedReason(
    error: unknown,
): { reason: string; extra: Record<string, unknown> } | null {
    const data = (error as { response?: { data?: Record<string, unknown> } })?.response?.data;
    const reason = String(data?.reason ?? '').trim();
    if (!reason) return null;
    const { success: _s, error: _e, reason: _r, ...extra } = data as Record<string, unknown>;
    return { reason, extra };
}

function unwrapS2000ConvertData(response: { data?: unknown }): Record<string, unknown> {
    const body = response.data as Record<string, unknown> | undefined;
    return (body?.data ?? body ?? {}) as Record<string, unknown>;
}

export const prepareS2000Convert = async (
    entityCd: string,
    rgstCd: string,
): Promise<S2000ConvertPrepareResult> => {
    try {
        const response = await apiClient.get<unknown>(
            `/register/my/s2000-convert/${encodeURIComponent(entityCd)}/prepare`,
            { params: { rgstCd } },
        );
        if (!response.success) {
            throw new Error(response.error?.message || 'Failed to prepare S2000 convert');
        }
        const payload = unwrapS2000ConvertData(response);
        const pending = payload.completePending as { shareholderIdx?: number } | undefined;
        if (pending?.shareholderIdx) {
            return { status: 'completePending', shareholderIdx: Number(pending.shareholderIdx) };
        }
        return { status: 'ready', data: payload as unknown as S2000ConvertPrepared };
    } catch (error) {
        const blocked = readS2000ConvertBlockedReason(error);
        if (blocked) return { status: 'blocked', ...blocked };
        throw error;
    }
};

export const markS2000ConvertContact = async (
    entityCd: string,
    contactCd: string,
): Promise<boolean> => {
    const response = await apiClient.post<unknown>(
        `/register/my/s2000-convert/${encodeURIComponent(entityCd)}/contact-created`,
        { contactCd },
    );
    if (!response.success) return false;
    return Boolean(unwrapS2000ConvertData(response).marked);
};

export type S2000ConvertCompleteResult =
    | { status: 'done'; hidden: number; upgraded: boolean }
    | { status: 'blocked'; reason: string; extra: Record<string, unknown> };

export const completeS2000Convert = async (
    entityCd: string,
    body: { rgstCd: string; shareholderIdx: number; createdContactCd?: string | null },
): Promise<S2000ConvertCompleteResult> => {
    try {
        const response = await apiClient.post<unknown>(
            `/register/my/s2000-convert/${encodeURIComponent(entityCd)}/complete`,
            body,
        );
        if (!response.success) {
            throw new Error(response.error?.message || 'Failed to complete S2000 convert');
        }
        const payload = unwrapS2000ConvertData(response);
        return { status: 'done', hidden: Number(payload.hidden || 0), upgraded: Boolean(payload.upgraded) };
    } catch (error) {
        const blocked = readS2000ConvertBlockedReason(error);
        if (blocked) return { status: 'blocked', ...blocked };
        throw error;
    }
};

export type UpdateMyRegisterEntityMemberShareholderPayload = {
    appointmentDate?: string | null;
    cessationDate?: string | null;
    occupation?: string;
    /** 법인 주주 지배 주체(AR Section F) — 같은 멤버의 모든 주식 행 CONTROLLED_BY (빈값 = 지움) */
    controlledBy?: string;
};

/**
 * MY Register — L_SHAREHOLDER_DETAIL 임명일(TRANSACTION_DATE)·퇴임일(SHARE_HOLDING_END_DATE) 저장
 */
export const updateMyRegisterEntityMemberShareholder = async (
    entityCd: string,
    shareholderIdx: number,
    payload: UpdateMyRegisterEntityMemberShareholderPayload,
): Promise<void> => {
    const response = await apiClient.put<unknown>(
        `/register/my/entity-members/${encodeURIComponent(entityCd)}/${shareholderIdx}`,
        payload,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update entity member shareholder');
    }
};

/**
 * MY Register — 퇴임 멤버 Global Contact 반영 후 profile history 스냅샷
 */
export const syncMyRegisterEntityMemberProfileHistory = async (
    entityCd: string,
    shareholderIdx: number,
    opts?: MyRegisterProfileHistorySyncOpts,
): Promise<{ historyId: string; action: string }> => {
    const response = await apiClient.post<unknown>(
        `/register/my/entity-members/${encodeURIComponent(entityCd)}/${shareholderIdx}/profile-history`,
        buildProfileHistorySyncPostBody(opts),
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to sync entity member profile history');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const payload = (body?.data ?? body ?? {}) as { historyId?: string; action?: string };
    return {
        historyId: String(payload.historyId ?? ''),
        action: String(payload.action ?? ''),
    };
};

export type MyRegisterEntityAllotmentRow = {
    SHAREHOLDER_IDX: number;
    CONTACT_CD: string;
    MEMBER_NAME: string;
    CONTACT_REG_NO: string;
    MEMBER_CODE?: string;
    CERT_NO?: string;
    TRAN_DATE: string;
    CLASS_OF_SHARES_TEXT: string;
    NUMBER_OF_SHARES: string;
    CESSATION_DATE?: string;
    _ENTITY_ALLOTMENT?: boolean;
    _RB_SOURCE?: string;
};

export const fetchMyRegisterEntityCharges = async (
    entityCd: string,
): Promise<Record<string, unknown>[]> => {
    const response = await apiClient.get<unknown>(
        `/register/my/entity-charges/${encodeURIComponent(entityCd)}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch entity charges');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const payload = (body?.data ?? body ?? response ?? {}) as {
        charges?: Record<string, unknown>[];
    };
    const list = payload.charges ?? (response as { charges?: Record<string, unknown>[] }).charges;
    return Array.isArray(list) ? list : [];
};

/**
 * MY Register — 담보 소프트 삭제 (L_ENTITY_CHARGE.IS_DEL = 1)
 * S2000(MIG_L_ENTITY_CHARGE) 담보는 대상이 아니다.
 */
export const deleteMyRegisterEntityCharge = async (
    entityCd: string,
    chargeNo: number,
): Promise<void> => {
    const response = await apiClient.delete<unknown>(
        `/register/my/entity-charges/${encodeURIComponent(entityCd)}/${chargeNo}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to delete entity charge');
    }
};

/**
 * MY Register — L_SHAREHOLDER_DETAIL (share_info.event_type = Allot) 목록
 */
export const fetchMyRegisterEntityAllotments = async (
    entityCd: string,
): Promise<MyRegisterEntityAllotmentRow[]> => {
    const response = await apiClient.get<unknown>(
        `/register/my/entity-allotments/${encodeURIComponent(entityCd)}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch entity allotments');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const payload = (body?.data ?? body ?? {}) as { allotments?: MyRegisterEntityAllotmentRow[] };
    return Array.isArray(payload.allotments) ? payload.allotments : [];
};

/**
 * MY Register — Allotment 상세 (L_SHAREHOLDER_DETAIL 1건)
 */
export const fetchMyRegisterEntityAllotmentDetail = async (
    entityCd: string,
    shareholderIdx: number,
): Promise<Record<string, unknown>> => {
    const response = await apiClient.get<unknown>(
        `/register/my/entity-allotments/${encodeURIComponent(entityCd)}/${shareholderIdx}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch entity allotment detail');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const payload = (body?.data ?? body ?? {}) as { detail?: Record<string, unknown> };
    return payload.detail ?? {};
};

/**
 * MY Register — Allotment share_info 저장 (SHAREHOLDER_JSON_DATA 병합)
 */
export const updateMyRegisterEntityAllotment = async (
    entityCd: string,
    shareholderIdx: number,
    payload: Record<string, unknown>,
): Promise<Record<string, unknown>> => {
    const response = await apiClient.put<unknown>(
        `/register/my/entity-allotments/${encodeURIComponent(entityCd)}/${shareholderIdx}`,
        payload,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update entity allotment');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const data = (body?.data ?? body ?? {}) as { detail?: Record<string, unknown> };
    return data.detail ?? {};
};

export type MyRegisterEntityTransferRow = {
    SHAREHOLDER_IDX: number;
    CONTACT_CD?: string;
    CURRENCY_CD?: string;
    CLASS_OF_SHARES?: string;
    CLASS_OF_SHARES_TEXT?: string;
    TRANSACTION_DATE?: string;
    CERT_NO?: string;
    TFOR_NAME?: string;
    TFOR_REG_NO?: string;
    TFOR_CONTACT_CD?: string;
    TFOR_CERTI_NO?: string;
    TFOR_NUMBER_OF_SHARES?: string;
    TEE_NAME?: string;
    TEE_REG_NO?: string;
    TEE_CONTACT_CD?: string;
    TEE_CERTI_NO?: string;
    TEE_NUMBER_OF_SHARES?: string;
    TRF_NUMBER_OF_SHARES?: string;
    _ENTITY_TRANSFER?: boolean;
    _RB_SOURCE?: string;
};

/**
 * MY Register — L_SHAREHOLDER_DETAIL (share_info.event_type = Transfer) 목록
 */
export const fetchMyRegisterEntityTransfers = async (
    entityCd: string,
): Promise<MyRegisterEntityTransferRow[]> => {
    const response = await apiClient.get<unknown>(
        `/register/my/entity-transfers/${encodeURIComponent(entityCd)}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch entity transfers');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const payload = (body?.data ?? body ?? {}) as { transfers?: MyRegisterEntityTransferRow[] };
    return Array.isArray(payload.transfers) ? payload.transfers : [];
};

/**
 * MY Register — Transfer 상세 (L_SHAREHOLDER_DETAIL 1건)
 */
export const fetchMyRegisterEntityTransferDetail = async (
    entityCd: string,
    shareholderIdx: number,
): Promise<Record<string, unknown>> => {
    const response = await apiClient.get<unknown>(
        `/register/my/entity-transfers/${encodeURIComponent(entityCd)}/${shareholderIdx}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch entity transfer detail');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const payload = (body?.data ?? body ?? {}) as { detail?: Record<string, unknown> };
    return payload.detail ?? {};
};

/**
 * MY Register — Transfer SHAREHOLDER_JSON_DATA 병합 저장
 */
export const updateMyRegisterEntityTransfer = async (
    entityCd: string,
    shareholderIdx: number,
    payload: Record<string, unknown>,
): Promise<Record<string, unknown>> => {
    const response = await apiClient.put<unknown>(
        `/register/my/entity-transfers/${encodeURIComponent(entityCd)}/${shareholderIdx}`,
        payload,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update entity transfer');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const data = (body?.data ?? body ?? {}) as { detail?: Record<string, unknown> };
    return data.detail ?? {};
};

export type MyRegisterEntityBeneficialOwnerRow = {
    CTRL_IDX: number;
    LEGAL_CTRL_IDX?: number | null;
    BENEFICIAL_CTRL_IDX?: number | null;
    ITEM_NO?: number | null;
    CONTACT_CD: string;
    LEGAL_CONTACT_CD?: string;
    BENEFICIAL_CONTACT_CD?: string;
    CONTACT_REG_NO: string;
    LEGAL_OWNER_NAME: string;
    LEGAL_OWNER_PERCENT: string;
    BENEFICIAL_OWNER_NAME: string;
    BENEFICIAL_OWNER_PERCENT: string;
    BECOME_DATE: string;
    CESSATION_DATE: string;
    _ENTITY_BENEFICIAL_OWNER?: boolean;
    _RB_SOURCE?: string;
};

/**
 * MY Register — L_BENEFICIALOWNER 베니피셜 오너 목록
 */
export const fetchMyRegisterEntityBeneficialOwners = async (
    entityCd: string,
): Promise<MyRegisterEntityBeneficialOwnerRow[]> => {
    const response = await apiClient.get<unknown>(
        `/register/my/entity-beneficial-owners/${encodeURIComponent(entityCd)}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch entity beneficial owners');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const payload = (body?.data ?? body ?? {}) as { beneficialOwners?: MyRegisterEntityBeneficialOwnerRow[] };
    return Array.isArray(payload.beneficialOwners) ? payload.beneficialOwners : [];
};

/**
 * MY Register — 베니피셜 오너 상세 (ControllersSection jsonData 형태 + BO_JSON_DATA)
 */
export const fetchMyRegisterEntityBeneficialOwnerDetail = async (
    entityCd: string,
    ctrlIdx: number,
): Promise<Record<string, unknown>> => {
    const response = await apiClient.get<unknown>(
        `/register/my/entity-beneficial-owners/${encodeURIComponent(entityCd)}/${ctrlIdx}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch entity beneficial owner detail');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const payload = (body?.data ?? body ?? {}) as { beneficialOwner?: Record<string, unknown> };
    return (payload.beneficialOwner as Record<string, unknown>) || {};
};

export type UpdateMyRegisterEntityBeneficialOwnerPayload = {
    appointmentDate?: string | null;
    cessationDate?: string | null;
    cessationReason?: string;
    confirmReceivedDate?: string | null;
    dateOfConfirmation?: string | null;
    dateOfChangeOfParticulars?: string | null;
    dateOfEntry?: string | null;
    occupation?: string;
    representBy?: string | null;
    classification?: Record<string, unknown>;
    ownership?: Record<string, unknown>;
    sm?: unknown;
    legalOwnerContactCd?: string | null;
    legalOwnerOwnPercent?: number | string | null;
};

export type MyRegisterLegalOwnerPickerOption = {
    contactCd: string;
    contactName: string;
    contactRegNo: string;
    roles?: string[];
};

export const fetchMyRegisterLegalOwnerPickerOptions = async (
    entityCd: string,
): Promise<MyRegisterLegalOwnerPickerOption[]> => {
    const response = await apiClient.get<unknown>(
        `/register/my/entity-beneficial-owners/${encodeURIComponent(entityCd)}/legal-owner-options`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch legal owner options');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const payload = (body?.data ?? body ?? {}) as { options?: MyRegisterLegalOwnerPickerOption[] };
    return Array.isArray(payload.options) ? payload.options : [];
};

/**
 * MY Register — L_BENEFICIALOWNER 임명일/퇴임일 + BO_JSON_DATA 저장
 */
export const updateMyRegisterEntityBeneficialOwner = async (
    entityCd: string,
    ctrlIdx: number,
    payload: UpdateMyRegisterEntityBeneficialOwnerPayload,
): Promise<void> => {
    const response = await apiClient.put<unknown>(
        `/register/my/entity-beneficial-owners/${encodeURIComponent(entityCd)}/${ctrlIdx}`,
        payload,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update entity beneficial owner');
    }
};

/**
 * MY Register — 퇴임 BO Global Contact 반영 후 profile history 스냅샷
 */
export const syncMyRegisterEntityBeneficialOwnerProfileHistory = async (
    entityCd: string,
    ctrlIdx: number,
    opts?: MyRegisterProfileHistorySyncOpts,
): Promise<{ historyId: string; action: string }> => {
    const response = await apiClient.post<unknown>(
        `/register/my/entity-beneficial-owners/${encodeURIComponent(entityCd)}/${ctrlIdx}/profile-history`,
        buildProfileHistorySyncPostBody(opts),
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to sync entity beneficial owner profile history');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const payload = (body?.data ?? body ?? {}) as { historyId?: string; action?: string };
    return {
        historyId: String(payload.historyId ?? ''),
        action: String(payload.action ?? ''),
    };
};

export const fetchMyRegisterEntityAddresses = async (
    entityCd: string,
): Promise<MyRegisterEntityAddressesPayload> => {
    const response = await apiClient.get<unknown>(
        `/register/my/entity-addresses/${encodeURIComponent(entityCd)}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch entity addresses');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const payload = (body?.data ?? body ?? {}) as MyRegisterEntityAddressesPayload;
    return {
        registeredAddress: (payload.registeredAddress as Record<string, unknown>) || {},
        alternateAddress: (payload.alternateAddress as Record<string, unknown>) || {},
        membersKeptAddress: (payload.membersKeptAddress as Record<string, unknown>) || {},
        financialRecordsKeptAddress: (payload.financialRecordsKeptAddress as Record<string, unknown>) || {},
        entityBranches: Array.isArray(payload.entityBranches) ? payload.entityBranches : [],
    };
};

export const fetchClientRegisterSourceFlags = async (): Promise<ClientRegisterSourceFlags> => {
    const response = await apiClient.get<unknown>('/register/book/client-source-flags');
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch client register source flags');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const payload =
        (body as any)?.data?.data
        ?? (body as any)?.data
        ?? body
        ?? {};
    return {
        showSpcFilter: !!(payload as ClientRegisterSourceFlags).showSpcFilter,
        showS2000Filter: !!(payload as ClientRegisterSourceFlags).showS2000Filter,
    };
};

export const fetchContactKeyMap = async (
    keyTypeName: string,
    opts?: { countryCode?: string }
): Promise<string[]> => {
    const params: Record<string, any> = { keyTypeName };
    if (opts?.countryCode) params.countryCode = opts.countryCode;
    const response = await apiClient.get<any>('/register/book/key-map', { params });
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch contact key map');
    }

    // apiClient/백엔드 wrapper 차이를 전부 흡수
    // - 케이스 A: { data: { data: {...} } }
    // - 케이스 B: { data: {...} }
    // - 케이스 C: {...}
    const body = response.data;
    const payload =
        body?.data?.data
        ?? body?.data
        ?? body
        ?? {};

    const keys = payload?.keysNormalized ?? payload?.keys ?? [];
    if (!Array.isArray(keys)) return [];
    return keys
        .map((k: any) => String(k).trim().toUpperCase())
        .filter((k: string) => k !== '');
};

/**
 * Update a single address record in the register book JSON file
 */
export const updateRegisterBookAddress = async (
    entityCd: string,
    addressId: number,
    data: Record<string, any>,
): Promise<void> => {
    const response = await apiClient.put(
        `/register/book/${encodeURIComponent(entityCd)}/address/${addressId}`,
        data,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update address');
    }
};

/**
 * Add a new address record to the register book JSON file
 */
export const addRegisterBookAddress = async (
    entityCd: string,
    data: Record<string, any>,
): Promise<void> => {
    const response = await apiClient.post(
        `/register/book/${encodeURIComponent(entityCd)}/address`,
        data,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to add address');
    }
};

/**
 * Add a record to a register book section (officers, members, etc.)
 * sectionKey: e.g. '5_Secretaries', '6_Directors', '7_Managers', '8_Members'
 */
export const addRegisterBookRecord = async (
    entityCd: string,
    sectionKey: string,
    data: Record<string, any>,
): Promise<void> => {
    const response = await apiClient.post(
        `/register/book/${encodeURIComponent(entityCd)}/section/${encodeURIComponent(sectionKey)}`,
        data,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to add record');
    }
};

/**
 * Update a record in a register book section
 */
export const updateRegisterBookRecord = async (
    entityCd: string,
    sectionKey: string,
    data: Record<string, any>,
): Promise<void> => {
    const response = await apiClient.put(
        `/register/book/${encodeURIComponent(entityCd)}/section/${encodeURIComponent(sectionKey)}`,
        data,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update record');
    }
};

/**
 * Delete an address record from the register book JSON file
 */
export const deleteRegisterBookAddress = async (
    entityCd: string,
    addressId: number,
): Promise<void> => {
    const response = await apiClient.delete(
        `/register/book/${encodeURIComponent(entityCd)}/address/${addressId}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to delete address');
    }
};

/**
 * Delete a record from a register book section
 */
export const deleteRegisterBookRecord = async (
    entityCd: string,
    sectionKey: string,
    data: Record<string, any>,
): Promise<void> => {
    const response = await apiClient.delete(
        `/register/book/${encodeURIComponent(entityCd)}/section/${encodeURIComponent(sectionKey)}`,
        { data },
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to delete record');
    }
};

export type MyRegisterEntityDirectorShipRow = {
    DS_IDX: number;
    CONTACT_CD?: string;
    APPOINTMENT_DATE?: string;
    COMPANY_NAME: string;
    IS_PUBLIC_COMPANY?: number | boolean;
    SHOW_IN_FORM49?: number | boolean;
    SHOW_IN_ANNUAL_RETURN?: number | boolean;
    REG_NO?: string;
    REG_NO_OLD?: string;
};

export const fetchMyRegisterEntityDirectorShips = async (
    entityCd: string,
    contactCd: string,
): Promise<MyRegisterEntityDirectorShipRow[]> => {
    const response = await apiClient.get<unknown>(
        `/register/my/entity-director-ships/${encodeURIComponent(entityCd)}/contact/${encodeURIComponent(contactCd)}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch other directorships');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const payload = (body?.data ?? body ?? {}) as { directorships?: MyRegisterEntityDirectorShipRow[] };
    return Array.isArray(payload.directorships) ? payload.directorships : [];
};

export const createMyRegisterEntityDirectorShip = async (
    entityCd: string,
    contactCd: string,
    payload: Record<string, unknown>,
): Promise<MyRegisterEntityDirectorShipRow> => {
    const response = await apiClient.post<unknown>(
        `/register/my/entity-director-ships/${encodeURIComponent(entityCd)}/contact/${encodeURIComponent(contactCd)}`,
        payload,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to create other directorship');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const data = (body?.data ?? body ?? {}) as { directorship?: MyRegisterEntityDirectorShipRow };
    return data.directorship as MyRegisterEntityDirectorShipRow;
};

export const updateMyRegisterEntityDirectorShip = async (
    entityCd: string,
    dsIdx: number,
    payload: Record<string, unknown>,
): Promise<MyRegisterEntityDirectorShipRow> => {
    const response = await apiClient.put<unknown>(
        `/register/my/entity-director-ships/${encodeURIComponent(entityCd)}/${dsIdx}`,
        payload,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update other directorship');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const data = (body?.data ?? body ?? {}) as { directorship?: MyRegisterEntityDirectorShipRow };
    return data.directorship as MyRegisterEntityDirectorShipRow;
};

export const deleteMyRegisterEntityDirectorShip = async (
    entityCd: string,
    dsIdx: number,
): Promise<void> => {
    const response = await apiClient.delete<unknown>(
        `/register/my/entity-director-ships/${encodeURIComponent(entityCd)}/${dsIdx}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to delete other directorship');
    }
};

/**
 * MY Register — L_SHAREHOLDER_DETAIL 중 L_OFFICER(B30027) 디렉터인 멤버만
 */
export const fetchMyRegisterEntityDirectorMembers = async (
    entityCd: string,
): Promise<MyRegisterEntityMemberRow[]> => {
    const response = await apiClient.get<unknown>(
        `/register/my/entity-director-members/${encodeURIComponent(entityCd)}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch director members');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const payload = (body?.data ?? body ?? {}) as { members?: MyRegisterEntityMemberRow[] };
    return Array.isArray(payload.members) ? payload.members : [];
};

/**
 * MY Register — L_OFFICER(B30027) 디렉터 중 L_SHAREHOLDER_DETAIL 지분 보유자
 */
export const fetchMyRegisterEntityDirectorShareholdings = async (
    entityCd: string,
): Promise<MyRegisterEntityMemberRow[]> => {
    const response = await apiClient.get<unknown>(
        `/register/my/entity-director-shareholdings/${encodeURIComponent(entityCd)}`,
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch director shareholdings');
    }
    const body = response.data as Record<string, unknown> | undefined;
    const payload = (body?.data ?? body ?? {}) as { shareholdings?: MyRegisterEntityMemberRow[] };
    return Array.isArray(payload.shareholdings) ? payload.shareholdings : [];
};
